<template>
  <div id="landing">
    <!-- Loading State -->
    <div v-if="loading && feed.length === 0" class="loading-container">
      <rtx-spinner class="page-spinner"/>
    </div>

    <!-- Main Content -->
    <div v-else class="content-wrapper">
      <site-header/>
      
      <main class="main-content">
        <!-- Single Entry View -->
        <transition name="slide-fade" mode="out-in">
          <div v-if="isSingleEntry" key="single" class="single-entry-container">
            <!-- 
               Passing props per requirements:
               1. showBackLink="false": because this is the "Landing version"
               2. truncateComments="true": ellipsize comments in this view
            -->
            <entry 
              :entry="feed[0]" 
              tileType="single"
              :showBackLink="false"
              :truncateComments="true"
            />
          </div>
          
          <!-- News Feed View -->
          <div v-else key="feed" class="feed-container">
            <div class="feed-header" v-if="!loading">
               <h1>{{ feedTitle }}</h1>
               <p class="feed-subtitle">{{ feedSubtitle }}</p>
            </div>
            <div class="feed-selector-wrapper">
              <feed-selector 
                :topics="feedTopics" 
                @topic-selected="handleTopicChange"
              />
            </div>
            <tiles :feed="feed"/>
          </div>
        </transition>
      </main>
      <site-footer ref="footerElement"/>
    </div>
  </div>
</template>

<script>
import { 
  getTrendingFeed, 
  getFullEntry, 
  getTopicEntries, 
  getReplaysFeed,
} from '../../backend/site.js';
import SiteHeader from "@/components/common/Header.vue";
import SiteFooter from "@/components/common/Footer.vue";
import RTXSpinner from "@/components/common/RTXSpinner.vue";
import Tiles from "@/components/site/Tiles.vue";
import Entry from "@/components/site/Entry.vue";
import FeedSelector from "@/components/site/FeedSelector.vue";

export default {
  name: 'Landing',
  components: {
    SiteHeader,
    SiteFooter,
    RTXSpinner,
    tiles: Tiles,
    entry: Entry,
    'feed-selector': FeedSelector,
  },
  data () {
    return {
      viewportSize: 0,
      feed: [],
      loading: true,
      error: null,
      feedTopics: [
        { id: 'trending', label: 'Trending', path: '/' },
        { id: 'replays', label: 'Replays', path: '/replays' }
      ],
    }
  },
  computed: {
    isSingleEntry() {
      return !!this.$route.params.eid;
    },
    currentEntry() {
      return this.isSingleEntry ? this.feed[0] : null;
    },
    feedTitle() {
      if (this.$route.path === '/') return 'Trending 🔥';
      if (this.$route.path === '/replays') return 'Replays ↺';
      return 'Trending';
    },
    feedSubtitle() {
      const date = new Date();
      const options = { weekday: 'long', month: 'long', day: 'numeric' };
      if (this.$route.params.topic) return `Top stories in ${this.$route.params.topic}`;
      return date.toLocaleDateString('en-US', options);
    },
    pageTitle() {
      if (this.currentEntry && this.currentEntry.entry) {
        return `${this.currentEntry.entry.content.title} - Commentoid`;
      }
      return 'Commentoid - Trending 🔥';
    },
    pageDescription() {
      if (this.currentEntry && this.currentEntry.entry) {
        return this.currentEntry.entry.content.description;
      }
      return 'Explore popular and trending comments.';
    },
    ogImage() {
      if (this.currentEntry && this.currentEntry.entry && this.currentEntry.entry.thumbnails) {
        return this.currentEntry.entry.thumbnails.high.url;
      }
      return 'https://www.commentoid.com/static/commentoid-og-image.png';
    }
  },
  metaInfo () {
    return {
      title: this.pageTitle,
      meta: [ 
        { name: 'description', content: this.pageDescription },
        { property: 'og:title', content: this.pageTitle },
        { property: 'og:description', content: this.pageDescription },
        { property: 'og:image', content: this.ogImage },
        { property: 'twitter:card', content: 'summary_large_image' }
      ]
    }
  },
  watch: {
    '$route': 'fetchData'
  },
  created() {
    window.addEventListener('resize', this.updateViewportSize);
    this.updateViewportSize();
    this.fetchData();
  },
  destroyed() {
    window.removeEventListener('resize', this.updateViewportSize);
  },
  methods: {
    updateViewportSize () {
      this.viewportSize = window.innerWidth;
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      this.feed = []; 

      const urlId = this.$route.params.eid;
      const topic = this.$route.params.topic;
      const path = this.$route.path;

      try {
        let result;
        if (urlId) {
          result = await getFullEntry({ urlId });
          this.feed = [result.data.value];
        } else if (path === '/replays') {
          result = await getReplaysFeed({});
          this.feed = result.data.value || [];
        } /*else if (topic) { // TODO PROPERLY SUPPORT LATER
          result = await getTopicEntries({ topic });
          this.feed = result.data.value || [];
        } */else if (path === '/') {
          result = await getTrendingFeed({});
          this.feed = result.data.value || [];
        }
      } catch (err) {
        console.error('Data fetch error:', err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    handleTopicChange(topic) {
      console.log('User clicked:', topic.label);
      // Note: The component handles the actual window.location assignment
    }    
  }
}
</script>

<style scoped>
#landing { 
  min-height: 100vh;
  background-color: #fcfcfc; 
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.content-wrapper {
  max-width: 1200px; 
  margin: 0 auto;
  padding: 0 1.5rem;
}

.main-content {
  min-height: 80vh;
  padding-bottom: 4rem;
}

.loading-container {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feed-header {
  margin: 2rem 0 1rem 2rem;
  text-align: left;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 1rem;
}

.feed-header h1 {
  font-size: 2.5rem;
  font-weight: 800;
  color: #111;
  margin: 0;
  letter-spacing: -0.04em;
  line-height: 1;
}

.feed-subtitle {
  color: #888;
  margin-top: 0.5rem;
  font-size: 1.2rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.feed-selector-wrapper {
  margin: 0rem 2rem;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.content-wrapper {
  padding: 0 0rem;
}

</style>



// WEBPACK FOOTER //
// src/components/site/Landing.vue